import { useMutation } from "@apollo/client";
import ContentCopy from "@mui/icons-material/ContentCopy";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonAddAlt1 from "@mui/icons-material/PersonAddAlt1";
import PersonRemove from "@mui/icons-material/PersonRemove";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import ScoutIcon from "components/ScoutIcon";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { MAX_BULK_VIOLATION_IDS } from "encamp-shared/src/violations/bulkActions";
import { MAX_BULK_FIELD_NOTES_GATHER } from "encamp-shared/src/violations/fieldNotesContent";
import { HarmonizedViolationProgram } from "generated-graphql/graphql";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_ASSIGNEES,
  SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEES,
  SET_HARMONIZED_VIOLATIONS_DUPLICATE_OF,
} from "hooks/useHarmonizedViolations";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import { getUserFacingMessage } from "utils/errorUtils";

import { type ViolationRow } from "./columns";
import {
  type CanonicalCandidate,
  CanonicalPicker,
  differentPeriodAdvice,
  DifferentPeriodDialog,
  inDifferentReportingPeriods,
} from "./DuplicateOfEditor";
import { GatherFieldNotesDialog } from "./GatherFieldNotesDialog";
import {
  firstUserId,
  skippedCopiesWarning,
  useAnnounceAssignment,
} from "./violationAssigneePicker";

/** Which assignee a bulk action targets. */
type BulkAssignMode = "assignee" | "staffAssignee";

/** "1 violation" / "3 violations", for button labels and result messages. */
function violationCount(count: number): string {
  return pluralize("violation", count, true);
}

/**
 * The open dialog and the ids it will write, snapshotted when it opened so a
 * selection change behind the dialog cannot retarget the write.
 */
type DialogState =
  | { kind: "assign"; mode: BulkAssignMode; ids: string[] }
  | { kind: "gatherFieldNotes"; ids: string[] }
  // The facility every selected row shares, or null when they span several —
  // which is what the candidate list leads with. `program` narrows that list
  // instead of ordering it, and is read off the loaded rows alone; a selection
  // whose loaded rows span programs never opens.
  | {
      kind: "duplicate";
      ids: string[];
      facilityId: string | null;
      program: HarmonizedViolationProgram | null;
      // The loaded rows' discovery dates, for the reporting-period question.
      // Only the loaded ones, like the two guards in `openDuplicateDialog`.
      startDates: (string | null | undefined)[];
    };

/** Selected rows the grid still holds; an off-page selection has no row to read. */
function loadedRows(rows: readonly (ViolationRow | undefined)[]): ViolationRow[] {
  return rows.filter((row): row is ViolationRow => row !== undefined);
}

/**
 * How many offending rows a warning names before it summarizes. A selection can
 * hold hundreds, and a snackbar can show a handful.
 */
const MAX_NAMED_ROWS = 3;

/** "V-1, V-2, V-3 and 7 more" — enough to find them, short enough to read. */
function namedViolations(violationIds: string[]): string {
  const shown = violationIds.slice(0, MAX_NAMED_ROWS).join(", ");
  const remaining = violationIds.length - MAX_NAMED_ROWS;
  return remaining > 0 ? `${shown} and ${remaining} more` : shown;
}

/**
 * "Waste and Water" / "Air, Waste and Water" — there are only ever three. Named
 * the way the grid's Program column names them, so the user is looking for the
 * same words the message uses.
 */
function namedPrograms(programs: readonly HarmonizedViolationProgram[]): string {
  const labels = programs.map((program) => prettyPrintEnumValue(program));
  if (labels.length <= 2) return labels.join(" and ");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** The one facility every loaded row sits at, or null when they differ. */
function commonFacilityId(rows: readonly ViolationRow[]): string | null {
  const facilityIds = new Set(rows.map((row) => row.facility.id));
  if (facilityIds.size !== 1) {
    return null;
  }
  return rows[0]?.facility.id ?? null;
}

/**
 * Picks one user (single-select) for a bulk assignment. Staff mode searches
 * Encamp staff; customer mode searches the tenant's users — the same split the
 * violation drawer's two override pickers use. Snapshotted rows are assigned on
 * confirm, so a selection change behind the dialog cannot retarget the write.
 */
function BulkAssignDialog({
  mode,
  count,
  saving,
  onAssign,
  onClose,
}: {
  mode: BulkAssignMode;
  count: number;
  saving: boolean;
  onAssign: (userId: string) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<UserOption[] | null>(null);
  const userId = firstUserId(selected);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      title={mode === "staffAssignee" ? "Set Encamp Assignee on violations" : "Assign violations"}
      primaryActionButtonProps={{
        disabled: !userId || saving,
        onClick: () => userId && onAssign(userId),
        saveText: `Assign ${violationCount(count)}`,
        startIcon: null,
      }}
    >
      <MultipleUserPicker
        label={mode === "staffAssignee" ? "Encamp Assignee" : "Assignee"}
        multiple={false}
        staffOnly={mode === "staffAssignee"}
        value={selected}
        disabled={saving}
        onChange={setSelected}
      />
    </Dialog>
  );
}

/**
 * Points every snapshotted row at one canonical violation. The picker starts at
 * the facility the selection shares, offers only the selection's own EPA
 * program, and the server rejects the batch WHOLE if any of it breaks a rule, so
 * nothing is partially marked.
 *
 * The selection itself is excluded from the picker: the candidates are scoped to
 * the same program and to rows that are not already duplicates, which is the
 * pool the selection came from — so the canonical a user wants is very likely
 * selected too, and a self-reference rejects the whole batch.
 */
function BulkDuplicateDialog({
  tenantId,
  ids,
  facilityId,
  program,
  startDates,
  saving,
  onMark,
  onClose,
}: {
  tenantId: string;
  ids: string[];
  facilityId: string | null;
  program: HarmonizedViolationProgram | null;
  startDates: (string | null | undefined)[];
  saving: boolean;
  onMark: (duplicateOfId: string) => void;
  onClose: () => void;
}) {
  const [canonical, setCanonical] = useState<CanonicalCandidate | null>(null);
  // The canonical waiting on the different-period question, or null when none is.
  const [confirming, setConfirming] = useState<CanonicalCandidate | null>(null);
  const exclude = useMemo(
    () => ({
      ids,
      note: "(in your selection)",
      constraint: "and not one of the violations you are marking",
    }),
    [ids]
  );

  // Counted rather than tested for existence: a selection spans many periods, so
  // "how many of these are not this" is the question, not "is this one". Only
  // the loaded rows can be counted, like the two guards in `openDuplicateDialog`
  // — a select-all whose differing row never loaded goes unasked.
  const differingCount = canonical
    ? startDates.filter((date) => inDifferentReportingPeriods(date, canonical.violationStartDate))
        .length
    : 0;

  const mark = (picked: CanonicalCandidate) => {
    if (differingCount > 0) {
      setConfirming(picked);
      return;
    }
    onMark(picked.id);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      title="Mark violations as duplicates"
      primaryActionButtonProps={{
        disabled: !canonical || saving,
        onClick: () => canonical && mark(canonical),
        saveText: `Mark ${violationCount(ids.length)}`,
        startIcon: null,
      }}
    >
      <CanonicalPicker
        tenantId={tenantId}
        facilityId={facilityId}
        program={program}
        exclude={exclude}
        value={canonical}
        onChange={setCanonical}
        disabled={saving}
      />
      {confirming ? (
        <DifferentPeriodDialog
          saving={saving}
          confirmLabel={`Yes, mark ${violationCount(ids.length)} as duplicates`}
          // The parent unmounts both dialogs when the write settles, so nothing
          // resets here; `saving` is what turns this button into its loading state.
          onConfirm={async () => onMark(confirming.id)}
          // Declining returns to the picker with the canonical still chosen, so
          // the user can swap it for one in the right period rather than restart.
          onCancel={() => setConfirming(null)}
        >
          {differingCount} of the {violationCount(ids.length)} you are marking{" "}
          {differingCount === 1 ? "is" : "are"} in a different reporting period from that violation.{" "}
          {differentPeriodAdvice(differingCount === 1 ? "that one" : "those")} You can still mark
          the whole selection.
        </DifferentPeriodDialog>
      ) : null}
    </Dialog>
  );
}

/**
 * The bulk writes behind the menu: the three mutations, the shared per-call cap,
 * and the result reporting. One `saving` covers all three, so the menu button
 * and both dialogs disable together while any is in flight.
 */
function useViolationBulkWrites({
  tenantId,
  clearSelection,
}: {
  tenantId: string;
  clearSelection: () => void;
}) {
  const alerts = useAlerts();
  const announceAssignment = useAnnounceAssignment();
  // A bulk change shifts the by-assignee donut AND the filtered row set (rows
  // assigned away from an active "assignee:" filter must leave the table), and
  // Apollo can recompute neither from the returned rows.
  const refetchQueries = [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS];
  const [setAssignees, { loading: savingAssignee }] = useMutation(
    SET_HARMONIZED_VIOLATION_ASSIGNEES,
    { refetchQueries }
  );
  const [setStaffAssignees, { loading: savingStaff }] = useMutation(
    SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEES,
    { refetchQueries }
  );
  const [setDuplicatesOf, { loading: savingDuplicates }] = useMutation(
    SET_HARMONIZED_VIOLATIONS_DUPLICATE_OF,
    { refetchQueries }
  );
  const saving = savingAssignee || savingStaff || savingDuplicates;

  /**
   * True when the selection is past the server's per-call cap, having said so —
   * otherwise the request fails wholesale and the user is told only to "try
   * again".
   */
  const refusedOverCap = (ids: string[]) => {
    if (ids.length <= MAX_BULK_VIOLATION_IDS) {
      return false;
    }
    alerts.error(
      `Select at most ${MAX_BULK_VIOLATION_IDS} violations at a time (you selected ${ids.length}).`
    );
    return true;
  };

  const assign = async (mode: BulkAssignMode, ids: string[], userId: string | null) => {
    if (ids.length === 0 || refusedOverCap(ids)) {
      return;
    }
    try {
      // Report the server's count, not the selection's — the backend skips rows
      // the caller can't see (held / out of tenant), so a stale selection would
      // otherwise claim more than actually changed.
      const result =
        mode === "assignee"
          ? (await setAssignees({ variables: { tenantId, ids, userId } })).data
              ?.setHarmonizedViolationAssignees
          : (await setStaffAssignees({ variables: { tenantId, ids, userId } })).data
              ?.setHarmonizedViolationStaffAssignees;
      const updated = result?.violations.length ?? 0;
      const skipped = {
        skippedCopyCount: result?.skippedCopyCount ?? 0,
        settingAssignee: !!userId,
      };
      if (updated === 0) {
        // Keep the selection so the user can investigate why nothing changed.
        const warning = skippedCopiesWarning(skipped.skippedCopyCount, skipped.settingAssignee);
        alerts.info(
          warning ? `No violations were updated. ${warning}` : "No violations were updated."
        );
        return;
      }
      const who = mode === "staffAssignee" ? "Encamp assignee" : "assignee";
      if (userId) {
        announceAssignment(
          mode === "staffAssignee"
            ? `Set the Encamp assignee on ${violationCount(updated)}`
            : `Assigned ${violationCount(updated)}`,
          skipped
        );
      } else {
        announceAssignment(`Cleared the ${who} on ${violationCount(updated)}`, skipped);
      }
      clearSelection();
    } catch (e) {
      // Leave the selection intact so the user can retry.
      alerts.error("Could not update the assignees. Try again.", e);
    }
  };

  const markDuplicates = async (ids: string[], duplicateOfId: string) => {
    if (ids.length === 0 || refusedOverCap(ids)) {
      return;
    }
    try {
      const { data } = await setDuplicatesOf({ variables: { tenantId, ids, duplicateOfId } });
      const updated = data?.setHarmonizedViolationsDuplicateOf.length ?? 0;
      if (updated === 0) {
        alerts.info("No violations were updated.");
        return;
      }
      alerts.success(`Marked ${violationCount(updated)} as duplicates`);
      clearSelection();
    } catch (e) {
      // Leave the selection intact so the user can retry. A rule the client
      // cannot pre-empt (a canonical someone else marked a duplicate meanwhile)
      // rejects the batch with its own wording, and an identical retry would
      // fail the same way — so say what the server said when it said anything.
      alerts.error(
        getUserFacingMessage(e) ?? "Could not mark the violations as duplicates. Try again.",
        e
      );
    }
  };

  return { saving, assign, markDuplicates };
}

/**
 * The violations grid's Bulk Actions dropdown: assign or clear the customer
 * assignee across the selected violations, plus the staff assignee for staff
 * users. Rendered as a grid command button, disabled until rows are selected.
 * Mirrors the staff Work queue's bulk-actions pattern. Must live inside the
 * grid's {@link GridSelectionProvider}.
 */
export function ViolationBulkActions({ tenantId }: { tenantId: string }) {
  const { isStaff } = useCurrentUser();
  // Ids drive every write: a selection can outlive the page it was made on (the
  // grid keeps off-page selections), and getSelectedRows() returns undefined for
  // those — reading `.id` off them would throw. Ids are always real. The rows
  // are read only to warn about what they show, via `loadedRows`.
  const { selectedCount, getSelectedIds, getSelectedRows, clearSelection } =
    useGridSelection<ViolationRow>();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const alerts = useAlerts();
  const { saving, assign, markDuplicates } = useViolationBulkWrites({
    tenantId,
    clearSelection,
  });

  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);

  const openGatherDialog = () => {
    closeMenu();
    const ids = getSelectedIds().map(String);
    if (ids.length > MAX_BULK_FIELD_NOTES_GATHER) {
      alerts.error(
        `Select at most ${MAX_BULK_FIELD_NOTES_GATHER} violations to gather field notes (you selected ${ids.length}).`
      );
      return;
    }
    setDialog({ kind: "gatherFieldNotes", ids });
  };

  const openDuplicateDialog = () => {
    const rows = loadedRows(getSelectedRows());
    // A row other violations already point at cannot itself become a duplicate,
    // and the server rejects the WHOLE batch for it — so name the offending rows
    // now instead of failing after the user has picked a canonical. Only the
    // loaded rows can be checked, so a select-all whose offending row sits on a
    // page that never loaded passes this guard and the server refuses the batch.
    const canonicals = rows.filter((row) => row.duplicateCount > 0);
    if (canonicals.length > 0) {
      alerts.error(
        `Deselect ${namedViolations(canonicals.map((row) => row.violationId))} — a violation that already has duplicates pointing at it cannot itself be marked as a duplicate.`
      );
      return;
    }
    // A violation may only be a duplicate within its own EPA program, so a
    // selection spanning two has no valid canonical at all. Refused rather than
    // narrowed to one program, which would mark rows the user did not mean.
    const programs = Array.from(new Set(rows.map((row) => row.sourceProgram))).sort();
    if (programs.length > 1) {
      alerts.error(
        `Select violations from one EPA program at a time — this selection spans ${namedPrograms(programs)}. A violation can only be a duplicate of another in the same program.`
      );
      return;
    }
    setDialog({
      kind: "duplicate",
      ids: getSelectedIds().map(String),
      facilityId: commonFacilityId(rows),
      // The loaded rows' program, or null when none is loaded to read one off.
      // A select-all whose loaded page happens to be single-program scopes the
      // picker to that program while the selection spans several, and the
      // server refuses the batch; its wording is what the user sees.
      program: programs[0] ?? null,
      startDates: rows.map((row) => row.violationStartDate),
    });
  };

  return (
    <>
      <Button
        id="violation-bulk-actions-button"
        aria-controls={menuOpen ? "violation-bulk-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        endIcon={<ExpandMore />}
        variant="contained"
        color="secondary"
        disabled={selectedCount === 0 || saving}
      >
        Bulk Actions
      </Button>
      <Menu
        id="violation-bulk-actions-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        // Prevent the menu from affecting body scroll, which trips the grid's ResizeObserver.
        disableScrollLock
        MenuListProps={{ "aria-labelledby": "violation-bulk-actions-button" }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setDialog({ kind: "assign", mode: "assignee", ids: getSelectedIds().map(String) });
          }}
        >
          <ListItemIcon>
            <PersonAddAlt1 fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign…</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            void assign("assignee", getSelectedIds().map(String), null);
          }}
        >
          <ListItemIcon>
            <PersonRemove fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear assignee</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeMenu();
            openDuplicateDialog();
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark as duplicates</ListItemText>
        </MenuItem>
        {isStaff
          ? [
              <Divider key="staff-divider" />,
              <MenuItem
                key="assign-staff"
                onClick={() => {
                  closeMenu();
                  setDialog({
                    kind: "assign",
                    mode: "staffAssignee",
                    ids: getSelectedIds().map(String),
                  });
                }}
              >
                <ListItemIcon>
                  <PersonAddAlt1 fontSize="small" />
                </ListItemIcon>
                <ListItemText>Set Encamp Assignee…</ListItemText>
              </MenuItem>,
              <MenuItem
                key="clear-staff"
                onClick={() => {
                  closeMenu();
                  void assign("staffAssignee", getSelectedIds().map(String), null);
                }}
              >
                <ListItemIcon>
                  <PersonRemove fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Encamp Assignee</ListItemText>
              </MenuItem>,
              <MenuItem key="gather-field-notes" onClick={openGatherDialog}>
                <ListItemIcon>
                  <ScoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Gather field notes…</ListItemText>
              </MenuItem>,
            ]
          : null}
      </Menu>
      {dialog?.kind === "assign" ? (
        <BulkAssignDialog
          mode={dialog.mode}
          count={dialog.ids.length}
          saving={saving}
          onAssign={(userId) => {
            const { mode, ids } = dialog;
            // Keep the dialog mounted through the mutation so its `saving` state
            // (disabled button + spinner) is reachable; close it once done.
            // `assign` catches its own errors, so `finally` always dismisses.
            void assign(mode, ids, userId).finally(() => setDialog(null));
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "gatherFieldNotes" ? (
        <GatherFieldNotesDialog
          tenantId={tenantId}
          ids={dialog.ids}
          onGathered={clearSelection}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "duplicate" ? (
        <BulkDuplicateDialog
          tenantId={tenantId}
          ids={dialog.ids}
          facilityId={dialog.facilityId}
          program={dialog.program}
          startDates={dialog.startDates}
          saving={saving}
          onMark={(duplicateOfId) => {
            const { ids } = dialog;
            void markDuplicates(ids, duplicateOfId).finally(() => setDialog(null));
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
